import { BlockNoteEditor } from '@blocknote/core';
import {
  FormattingToolbar,
  FormattingToolbarController,
  BlockTypeSelect,
  BlockTypeSelectItem,
  BasicTextStyleButton,
  CreateLinkButton,
  UnnestBlockButton,
  NestBlockButton,
  useBlockNoteEditor
} from '@blocknote/react';
import { useCallback } from 'react';
import Icon from '@/components/atoms/Icon';

const HeadingIcon = () => (
  <div>
    <span>H</span>
  </div>
);
const H1Icon = () => (
  <div>
    <span>H</span>
    <span style={{ fontSize: '70%' }}>1</span>
  </div>
);
const H2Icon = () => (
  <div>
    <span>H</span>
    <span style={{ fontSize: '70%' }}>2</span>
  </div>
);
const H3Icon = () => (
  <div>
    <span>H</span>
    <span style={{ fontSize: '70%' }}>3</span>
  </div>
);
const ListIcon = props => <Icon name="IconList" {...props} />;
const ListNumbersIcon = props => <Icon name="IconListNumbers" {...props} />;

export const blockTypeSelectItems = (): BlockTypeSelectItem[] => [
  {
    name: 'Paragraph',
    type: 'paragraph',
    icon: HeadingIcon,
    isSelected: block => block.type === 'paragraph'
  },
  {
    name: 'Heading 1',
    type: 'heading',
    props: { level: 1 },
    icon: H1Icon,
    isSelected: block => block.type === 'heading' && block.props?.level === 1
  },
  {
    name: 'Heading 2',
    type: 'heading',
    props: { level: 2 },
    icon: H2Icon,
    isSelected: block => block.type === 'heading' && block.props?.level === 2
  },
  {
    name: 'Heading 3',
    type: 'heading',
    props: { level: 3 },
    icon: H3Icon,
    isSelected: block => block.type === 'heading' && block.props?.level === 3
  },
  {
    name: 'Bullet List',
    type: 'bulletListItem',
    icon: ListIcon,
    isSelected: block => block.type === 'bulletListItem'
  },
  {
    name: 'Numbered List',
    type: 'numberedListItem',
    icon: ListNumbersIcon,
    isSelected: block => block.type === 'numberedListItem'
  }
];

export default function TextEditorFormattingToolbar() {
  const editor = useBlockNoteEditor();

  const isNestableBlock = useCallback((editor: BlockNoteEditor, variant: 'nest' | 'unnest') => {
    const block = editor?.getTextCursorPosition()?.block;

    const isListItem = block?.type === 'bulletListItem' || block?.type === 'numberedListItem';

    if (!isListItem) {
      return false;
    }

    const prevBlock = editor?.getTextCursorPosition()?.prevBlock;
    const isTopLevelBlock = editor.topLevelBlocks.includes(block);
    const isPreviousBlockListItem = prevBlock?.type === 'bulletListItem' || prevBlock?.type === 'numberedListItem';

    if (isTopLevelBlock && !isPreviousBlockListItem) {
      return false;
    }

    return variant === 'nest' ? editor.canNestBlock() : editor.canUnnestBlock();
  }, []);

  return (
    <FormattingToolbarController
      formattingToolbar={() => (
        <FormattingToolbar>
          <BlockTypeSelect items={blockTypeSelectItems()} />
          <BasicTextStyleButton basicTextStyle="bold" />
          <BasicTextStyleButton basicTextStyle="italic" />
          {isNestableBlock(editor, 'unnest') && <UnnestBlockButton />}
          {isNestableBlock(editor, 'nest') && <NestBlockButton />}
          <CreateLinkButton />
        </FormattingToolbar>
      )}
    />
  );
}
